import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiState } from '../../../types/api';
import type { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { ISignInResponse } from './authTypes';
import { persistAuthData } from './persistAuthData';

interface ICompleteMfaCredentials {
  sessionToken: string;
  mfaToken: string;
}

export interface ICompleteMfaRejectPayload extends ISignInResponse {
  unauthorized?: boolean;
}

export const completeMfaAsync = createAsyncThunk<
  ISignInResponse,
  ICompleteMfaCredentials,
  { rejectValue: ICompleteMfaRejectPayload }
>(
  'auth/completeMfa',
  async (
    { sessionToken, mfaToken },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const email = state.auth.value?.data?.email ?? '';

      const { data } = await api.post<ISignInResponse>(
        '/auth/login/complete-mfa',
        { sessionToken, mfaToken },
      );

      persistAuthData({
        user: data?.data?.user,
        token: data?.data?.token,
        email,
        dispatch,
      });

      return data;
    } catch (err) {
      const error = err as AxiosError<ISignInResponse>;
      const isUnauthorized = error?.response?.status === 401;

      dispatch(
        showAlert({
          status: 'error',
          description: isUnauthorized
            ? 'Your session has expired. Please sign in again.'
            : (error?.response?.data?.message ??
              'An error occurred while verifying your OTP, try again'),
        }),
      );

      return rejectWithValue({
        ...error.response?.data,
        unauthorized: isUnauthorized,
      });
    }
  },
);

const initialState: IApiState<ISignInResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const completeMfaSlice = createSlice({
  name: 'completeMfa',
  initialState,
  reducers: {
    resetCompleteMfa: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(completeMfaAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(completeMfaAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(completeMfaAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export const { resetCompleteMfa } = completeMfaSlice.actions;

export default completeMfaSlice.reducer;
